/**
 * Error Notification Components
 *
 * Toast notifications and alerts for displaying errors to users
 */

import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

/**
 * Notification types
 */
export type NotificationType = 'error' | 'warning' | 'info' | 'success'

/**
 * Notification data structure
 */
export interface Notification {
  id: string
  type: NotificationType
  title?: string
  message: string
  duration?: number
  persistent?: boolean
  actions?: NotificationAction[]
}

/**
 * Notification action
 */
export interface NotificationAction {
  label: string
  action: () => void
  variant?: 'default' | 'destructive' | 'outline'
}

/**
 * Error Alert Component
 */
interface ErrorAlertProps {
  error: Error | string
  onDismiss?: () => void
  showDetails?: boolean
  className?: string
}

export function ErrorAlert({
  error,
  onDismiss,
  showDetails = false,
  className,
}: ErrorAlertProps) {
  const [showFullDetails, setShowFullDetails] = useState(false)
  const errorMessage = typeof error === 'string' ? error : error.message

  return (
    <Alert variant='destructive' className={cn('relative', className)}>
      <AlertTriangle className='h-4 w-4' />
      <AlertDescription>
        <div className='space-y-2'>
          <div>{errorMessage}</div>

          {/* Technical details only in development */}
          {showDetails && typeof error !== 'string' && import.meta.env.DEV && (
            <div className='space-y-2'>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setShowFullDetails(!showFullDetails)}
                className='h-auto p-0 text-xs'
              >
                {showFullDetails ? 'Hide' : 'Show'} Technical Details
              </Button>

              {showFullDetails && (
                <div className='bg-muted p-2 rounded text-xs font-mono'>
                  <div>
                    <strong>Message:</strong> {error.message}
                  </div>
                  {error.stack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className='mt-1 whitespace-pre-wrap text-xs overflow-auto max-h-32'>
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {onDismiss && (
          <Button
            variant='ghost'
            size='sm'
            onClick={onDismiss}
            className='absolute top-2 right-2 h-auto p-1'
          >
            <X className='h-3 w-3' />
          </Button>
        )}
      </AlertDescription>
    </Alert>
  )
}

/**
 * Toast Notification Component
 */
interface ToastNotificationProps {
  notification: Notification
  onDismiss: (id: string) => void
}

export function ToastNotification({
  notification,
  onDismiss,
}: ToastNotificationProps) {
  const [isVisible, setIsVisible] = useState(true)
  const [progress, setProgress] = useState(100)

  useEffect(() => {
    if (!notification.persistent && notification.duration) {
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - 100 / (notification.duration! / 100)
          if (newProgress <= 0) {
            setIsVisible(false)
            setTimeout(() => onDismiss(notification.id), 300)
            return 0
          }
          return newProgress
        })
      }, 100)

      return () => clearInterval(interval)
    }
  }, [notification, onDismiss])

  const handleDismiss = () => {
    setIsVisible(false)
    setTimeout(() => onDismiss(notification.id), 300)
  }

  const icons = {
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
    success: CheckCircle,
  }

  const Icon = icons[notification.type]

  const colorClasses = {
    error: 'bg-destructive text-destructive-foreground',
    warning: 'bg-orange-500 text-white',
    info: 'bg-blue-500 text-white',
    success: 'bg-green-500 text-white',
  }

  return (
    <div
      className={cn(
        'relative min-w-[300px] max-w-[500px] p-4 rounded-lg shadow-lg transition-all duration-300',
        colorClasses[notification.type],
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
    >
      {/* Progress bar */}
      {!notification.persistent && notification.duration && (
        <div className='absolute bottom-0 left-0 h-1 bg-black/20 rounded-b-lg'>
          <div
            className='h-full bg-white/60 rounded-b-lg transition-all duration-100 ease-linear'
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Content */}
      <div className='flex items-start gap-3'>
        <Icon className='h-5 w-5 mt-0.5 flex-shrink-0' />

        <div className='flex-1 space-y-1'>
          {notification.title && (
            <div className='font-medium text-sm'>{notification.title}</div>
          )}
          <div className='text-sm opacity-90'>{notification.message}</div>

          {/* Actions */}
          {notification.actions && notification.actions.length > 0 && (
            <div className='flex gap-2 mt-3'>
              {notification.actions.map((action, index) => (
                <Button
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  key={index}
                  variant={action.variant || 'outline'}
                  size='sm'
                  onClick={action.action}
                  className='h-8 text-xs'
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        <Button
          variant='ghost'
          size='sm'
          onClick={handleDismiss}
          className='h-auto p-1 text-white/70 hover:text-white'
        >
          <X className='h-4 w-4' />
        </Button>
      </div>
    </div>
  )
}

/**
 * Toast Container Component
 */
interface ToastContainerProps {
  notifications: Notification[]
  onDismiss: (id: string) => void
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
}

export function ToastContainer({
  notifications,
  onDismiss,
  position = 'top-right',
}: ToastContainerProps) {
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  }

  return (
    <div className={cn('fixed z-50 space-y-2', positionClasses[position])}>
      {notifications.map(notification => (
        <ToastNotification
          key={notification.id}
          notification={notification}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  )
}

/**
 * Inline Error Message Component
 */
interface InlineErrorProps {
  error?: Error | string | null
  className?: string
}

export function InlineError({ error, className }: InlineErrorProps) {
  if (!error) return null

  const message = typeof error === 'string' ? error : error.message

  return (
    <div
      className={cn(
        'flex items-center gap-2 text-sm text-destructive',
        className
      )}
    >
      <AlertCircle className='h-4 w-4 flex-shrink-0' />
      <span>{message}</span>
    </div>
  )
}

/**
 * Loading Error Component (for async operations)
 */
interface LoadingErrorProps {
  error: Error | null
  onRetry?: () => void
  retryLabel?: string
  className?: string
}

export function LoadingError({
  error,
  onRetry,
  retryLabel = 'Try Again',
  className,
}: LoadingErrorProps) {
  if (!error) return null

  const message = error.message

  return (
    <div className={cn('text-center p-6 space-y-4', className)}>
      <div className='flex justify-center'>
        <AlertTriangle className='h-12 w-12 text-destructive' />
      </div>

      <div className='space-y-2'>
        <h3 className='font-medium text-lg'>Something went wrong</h3>
        <p className='text-muted-foreground text-sm max-w-md mx-auto'>
          {message}
        </p>
      </div>

      {onRetry && (
        <Button onClick={onRetry} variant='outline'>
          {retryLabel}
        </Button>
      )}
    </div>
  )
}

/**
 * Form Field Error Component
 */
interface FieldErrorProps {
  error?: string | string[]
  className?: string
}

export function FieldError({ error, className }: FieldErrorProps) {
  if (!error) return null

  const errors = Array.isArray(error) ? error : [error]

  return (
    <div className={cn('space-y-1', className)}>
      {errors.map((errorMessage, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          key={index}
          className='flex items-center gap-2 text-sm text-destructive'
        >
          <AlertCircle className='h-3 w-3 flex-shrink-0' />
          <span>{errorMessage}</span>
        </div>
      ))}
    </div>
  )
}
